import React from 'react';
import { Card, CardContent, Typography, Box, IconButton, useTheme, alpha, Skeleton } from '@mui/material';
import { Fullscreen as FullscreenIcon } from '@mui/icons-material';

interface ProfessionalChartCardProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  onFullscreen?: () => void;
  loading?: boolean;
  height?: string | number;
  headerAction?: React.ReactNode;
}

const ProfessionalChartCard: React.FC<ProfessionalChartCardProps> = ({
  title,
  subtitle,
  children,
  onFullscreen,
  loading = false,
  height = 400,
  headerAction,
}) => {
  const theme = useTheme();

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        background: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        borderRadius: 3,
        overflow: 'hidden',
        transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.primary.main, 0.6)}, transparent)`,
          opacity: 0,
          transition: 'opacity 0.3s ease-in-out',
        },
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 16px 32px ${alpha(theme.palette.primary.main, 0.12)}, 0 0 0 1px ${alpha(theme.palette.primary.main, 0.05)}`,
          background: 'rgba(255, 255, 255, 0.95)',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          '&::before': {
            opacity: 1,
          },
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            mb: 3,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: subtitle ? 0.5 : 0,
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            {headerAction}
            {onFullscreen && (
              <IconButton
                size="small"
                onClick={onFullscreen}
                className="top-panel-no-pdf"
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: theme.palette.primary.main,
                    background: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                <FullscreenIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Chart Content */}
        <Box
          sx={{
            height: typeof height === 'number' ? `${height}px` : height,
            position: 'relative',
          }}
        >
          {loading ? (
            <Skeleton
              variant="rectangular"
              width="100%"
              height="100%"
              animation="wave"
              sx={{ borderRadius: 2 }}
            />
          ) : (
            children
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProfessionalChartCard;
